import { useMemo, useState } from "react";
import { connect } from "react-redux";
import { Box, Typography } from "@mui/material";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppCheckbox from "../Common/FormInputs/AppCheckbox";
import EmptyState from "../Common/EmptyState/EmptyState";
import { useDesignTokens } from "../../theme/useDesignTokens";
import customConfig from "../../config";
import { menuConfig } from "../../config/menuConfig";
import {
  buildMenuPermissionTree,
  getCheckedPagesForRole,
  getRoleOptions,
} from "../../utils/buildPermissionMatrix";
import {
  PERMISSION_ACCESS_DENIED,
  PERMISSION_MANAGEMENT_DESC,
  PERMISSION_MANAGEMENT_TITLE,
  PERMISSION_NO_PAGES,
  PERMISSION_VIEW_ROLE_HINT,
} from "./permissionConstants";

const PermissionManagement = ({ userRoles = [] }) => {
  const { colors, font, radius } = useDesignTokens();
  const borderColor = colors?.ghost?.light || "#E0E0E0";
  const surfaceWhite = colors?.colors?.white || "#FFFFFF";
  const headerBg = colors?.secondary?.bg || colors?.disabled?.bg || "#F7F7F7";
  const textMain = colors?.secondary?.main || "#333333";
  const textMuted = colors?.disabled?.main || "#9E9E9E";
  const selectedBg = colors?.disabled?.bg || "#F5F5F5";
  const canAccessPermissionManagement = userRoles?.includes(
    customConfig.roleCloudAdmin
  );

  const roleOptions = useMemo(() => getRoleOptions(), []);
  const menuTree = useMemo(() => buildMenuPermissionTree(menuConfig), []);

  const [selectedRole, setSelectedRole] = useState(customConfig.roleCloudAdmin);

  const checkedPages = useMemo(
    () => getCheckedPagesForRole(selectedRole, menuConfig),
    [selectedRole]
  );

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  const getSectionCheckState = (section) => {
    const pageKeys = section.pages.map((p) => p.key);
    const checkedCount = pageKeys.filter((key) => checkedPages.has(key)).length;
    return {
      checked: checkedCount > 0 && checkedCount === pageKeys.length,
      indeterminate: checkedCount > 0 && checkedCount < pageKeys.length,
    };
  };

  if (!canAccessPermissionManagement) {
    return (
      <EmptyState minHeight="80vh" message={PERMISSION_ACCESS_DENIED} />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        pt: 2,
        pb: 4,
        px: 0.5,
      }}
    >
      <Box
        sx={{
          mb: 2.5,
          pb: 2.5,
          borderBottom: `3px solid ${borderColor}`,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: font?.weight?.fontW700,
              mb: 0.5,
              color: textMain,
            }}
          >
            {PERMISSION_MANAGEMENT_TITLE}
          </Typography>
          <Typography variant="body2" sx={{ color: textMuted }}>
            {PERMISSION_MANAGEMENT_DESC}
          </Typography>
        </Box>

        <Box sx={{ flex: "1 1 220px", minWidth: 200, maxWidth: 280 }}>
          <FormInput
            type="select"
            name="selectedRole"
            label="Role"
            value={selectedRole}
            onChange={handleRoleChange}
            options={roleOptions}
            helperText={PERMISSION_VIEW_ROLE_HINT}
          />
        </Box>
      </Box>

      {!menuTree.length ? (
        <EmptyState minHeight="40vh" message={PERMISSION_NO_PAGES} />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
          }}
        >
          {menuTree.map((section) => {
            const { checked, indeterminate } = getSectionCheckState(section);
            const selectedCount = section.pages.filter((p) =>
              checkedPages.has(p.key)
            ).length;

            return (
              <Box
                key={section.id}
                sx={{
                  backgroundColor: surfaceWhite,
                  border: `1px solid ${borderColor}`,
                  borderRadius: radius?.epx || radius?.default || "8px",
                  overflow: "visible",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 2,
                    py: 0.75,
                    minHeight: 42,
                    backgroundColor: headerBg,
                    borderBottom: `1px solid ${borderColor}`,
                  }}
                >
                  <AppCheckbox
                    checked={checked}
                    indeterminate={indeterminate}
                    disabled
                    size="small"
                    label={
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: font?.weight?.fontW700 || 700,
                          fontSize: font?.size?.fontSz14 || "0.875rem",
                          color: textMain,
                          ml: 0.25,
                        }}
                      >
                        {section.name}
                      </Typography>
                    }
                    checkboxSx={{ p: 0.5 }}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        overflow: "visible",
                      },
                    }}
                  />
                  <Box sx={{ flex: 1 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: textMuted,
                      fontWeight: font?.weight?.fontW500 || 500,
                      pr: 0.5,
                    }}
                  >
                    {selectedCount} of {section.pages.length}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: { xs: 1, sm: 2 },
                    px: 2,
                    py: 1.25,
                    rowGap: 0.75,
                    backgroundColor: surfaceWhite,
                  }}
                >
                  {section.pages.map((page) => {
                    const isChecked = checkedPages.has(page.key);
                    return (
                      <Box
                        key={page.key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          minHeight: 34,
                          px: 1,
                          py: 0.25,
                          borderRadius: radius?.fpx || "4px",
                          backgroundColor: isChecked
                            ? selectedBg
                            : "transparent",
                        }}
                      >
                        <AppCheckbox
                          checked={isChecked}
                          disabled
                          size="small"
                          label={page.name}
                          checkboxSx={{ p: 0.5 }}
                          labelSx={{
                            fontSize: font?.size?.fontSz14 || "0.875rem",
                            lineHeight: 1.3,
                            whiteSpace: "nowrap",
                            color: textMain,
                            fontWeight: font?.weight?.fontW500 || 500,
                          }}
                          sx={{
                            mr: 0,
                            "& .MuiFormControlLabel-label": {
                              overflow: "visible",
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  userRoles: state.auth.userRoles,
});

export default connect(mapStateToProps)(PermissionManagement);
